import { Heading, LuIcon, Separator } from "@overmap-ai/blocks"
import { type IssueTypeAttachment, OrganizationAccessLevel, type Stored } from "@overmap-ai/common"
import { selectAttachmentsOfIssueType, selectIssueTypeById } from "@overmap-ai/core"
import { getRouteApi } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { AttachmentList } from "../../components/AttachmentList/AttachmentList"
import { AttachmentButton } from "../../components/Buttons/AttachmentButton"
import { ViewContainer } from "../../components/Containers/ViewContainer"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/issue-types/$issueTypeId/attachments")

export const IssueTypeSettingsAttachmentsView = memo(() => {
	const { issueTypeId } = routeApi.useParams()
	const issueType = useAppSelector(selectIssueTypeById(issueTypeId))

	if (!issueType) {
		throw new Error(`expected issueType to exist, got ${issueType}.`)
	}

	const attachments = useShallowEqaulSelector(selectAttachmentsOfIssueType(issueType.uuid))
	const openConfirmDeleteDialog = useConfirmDeleteDialog()

	const handleAttachFiles = useCallback(
		(files: File[]) => {
			return sdk.issueTypeAttachments.bulkAdd(
				files.map((file) => ({
					issue_type: issueType.uuid,
					file,
				})),
			)
		},
		[issueType.uuid],
	)

	const handleDeleteAttachment = useCallback(
		(fileAttachment: Stored<IssueTypeAttachment>) => {
			openConfirmDeleteDialog({
				title: "Delete attachment",
				description: "The attachment will be deleted immediately. This action cannot be undone.",
				onAction: () => sdk.issueTypeAttachments.delete(fileAttachment.uuid),
			})
		},
		[openConfirmDeleteDialog],
	)

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4">
				<div className="flex items-center justify-between gap-2">
					<Heading size="xl">Attachments</Heading>

					<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
						<AttachmentButton size="sm" variant="surface" onAttachFiles={handleAttachFiles}>
							<LuIcon icon={modelIconMapping.add} />
							Add
						</AttachmentButton>
					</OrganizationAccessRequired>
				</div>

				<Separator size="full" />

				<AttachmentList
					attachments={attachments}
					onDelete={handleDeleteAttachment}
					service={sdk.issueTypeAttachments}
				/>
			</section>
		</ViewContainer>
	)
})
IssueTypeSettingsAttachmentsView.displayName = "IssueTypeSettingsAttachmentsView"
